/**
 * COMPANY SETTINGS (TS-95) — Sezione "Azienda" delle Impostazioni: logo
 * aziendale e paletta. Sola lettura per gli utenti, modificabile dagli
 * amministratori (l'enforce reale resta lato backend: /company/* è admin-only).
 *
 * Il logo riusa ImageUploadField (stessa UX del cambio foto profilo); al
 * salvataggio il thunk updateCompanyLogo aggiorna companyLogoURL sullo user in
 * store, quindi l'header si aggiorna da solo.
 *
 * La PALETTA si configura con due soli colori — primario e secondario — perché
 * tutti gli altri si calcolano da questi (vedi `derivaPalette`): i testi sopra
 * al colore li decide il contrasto, non il gusto, e le tinte chiare/scure sono
 * miscele. Salvando, il thunk scrive la palette in store e il ThemeManager la
 * riapplica: il colore cambia sotto gli occhi, senza ricaricare.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useToast } from '../useToast';
import { resolveLiferayImageURL } from '../../api/portrait';
import { ImageUploadField } from './ImageUploadField';
import { ColorPicker } from '../ColorPicker';
import { updateCompanyLogo, updateCompanyName, resetCompanyLogo } from '../../auth/authSlice';
import { resetCompanyPalette, updateCompanyPalette } from '../../themeSlice';
import {
  DEFAULT_PALETTE,
  derivaPalette,
  derivedSecondary,
  mixSrgb,
  type BrandPalette,
} from '../../brandPalette';

/** Un colore utilizzabile: `#rgb` o `#rrggbb`. */
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Il valore da mostrare nel campo "colore secondario": VUOTO significa "derivalo
 * dal primario". La palette in store ha sempre un secondario concreto — se non è
 * stato scelto, l'ha già derivato — quindi per sapere se era una scelta o un
 * calcolo lo si confronta con quel che si otterrebbe dal primario. Se coincidono,
 * per l'utente il campo è vuoto; e se coincidono per caso non cambia nulla,
 * perché il colore è comunque lo stesso.
 */
function secondaryFieldValue(palette: BrandPalette): string {
  return palette.secondary === derivedSecondary(palette.primary) ? '' : palette.secondary;
}

export function CompanySettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const palette = useAppSelector((state) => state.theme.palette) ?? DEFAULT_PALETTE;
  const themeStatus = useAppSelector((state) => state.theme.status);
  // I campi della paletta si inizializzano UNA volta sola, quando montano: se
  // montassero prima che la GET abbia risposto si fisserebbero sui predefiniti e
  // ci resterebbero anche dopo l'arrivo dei colori veri, perche' useState ignora
  // i valori successivi al primo render. Quindi si aspetta.
  const paletteLoaded = themeStatus === 'succeeded' || themeStatus === 'failed';
  const isAdmin = user?.administrator ?? false;
  const hasCustomLogo = Boolean(user?.companyLogoURL);

  // Ritaglio corrente in attesa di salvataggio (data URL) o null.
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.companyName ?? '');
  const [savingName, setSavingName] = useState(false);
  const nameChanged = name.trim() !== (user?.companyName ?? '');

  // Logo attuale (custom risolto verso l'istanza) oppure il default FeelHippo.
  const currentLogo = resolveLiferayImageURL(user?.companyLogoURL) ?? '/logo.svg';

  async function handleSave() {
    if (!logoPreview) return;
    setSaving(true);
    try {
      // data URL -> base64 puro (drop del prefisso), come la foto profilo.
      await dispatch(updateCompanyLogo(logoPreview.replace(/^data:[^,]+,/, ''))).unwrap();
      setLogoPreview(null);
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveName() {
    setSavingName(true);
    try {
      setName(await dispatch(updateCompanyName(name.trim())).unwrap());
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSavingName(false);
    }
  }

  async function handleReset() {
    setSaving(true);
    try {
      await dispatch(resetCompanyLogo()).unwrap();
      setLogoPreview(null);
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-md">
      <h2>{t('settings.company.nameTitle')}</h2>
      <p className="text-muted">{t('settings.company.nameHint')}</p>

      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        label={t('settings.company.name')}
        size="small"
        disabled={!isAdmin}
        slotProps={{ htmlInput: { maxLength: 75 } }}
        className="company-settings__name"
      />

      {isAdmin && (
        <div className="flex gap-sm">
          <Button
            variant="contained"
            onClick={handleSaveName}
            disabled={!nameChanged || !name.trim() || savingName}
          >
            {t('common.save')}
          </Button>
        </div>
      )}

      <h2 className="company-settings__section">{t('settings.company.logoTitle')}</h2>
      <p className="text-muted">{t('settings.company.logoHint')}</p>

      <ImageUploadField
        value={logoPreview}
        onChange={setLogoPreview}
        currentSrc={currentLogo}
        selectLabel={t('settings.company.selectLogo')}
        repositionLabel={t('settings.company.reposition')}
        alt={t('brand.name')}
        disabled={!isAdmin}
        shape="rounded"
      />

      {isAdmin ? (
        <div className="flex gap-sm">
          <Button variant="contained" onClick={handleSave} disabled={!logoPreview || saving}>
            {t('common.save')}
          </Button>
          <Button variant="text" onClick={handleReset} disabled={saving || !hasCustomLogo}>
            {t('settings.company.reset')}
          </Button>
        </div>
      ) : (
        <p className="text-muted text-sm">{t('settings.company.readonly')}</p>
      )}

      <h2 className="company-settings__section">{t('settings.company.paletteTitle')}</h2>
      <p className="text-muted">{t('settings.company.paletteHint')}</p>

      {paletteLoaded ? (
        <CompanyPalette palette={palette} isAdmin={isAdmin} />
      ) : (
        <p className="text-muted text-sm">{t('common.loading')}</p>
      )}
    </div>
  );
}

/**
 * I due campi della paletta, l'anteprima dei derivati e i comandi. Sta in un
 * componente a parte perche' i suoi useState prendono il valore iniziale dalla
 * palette, e un valore iniziale si legge una volta sola: montarlo prima che la
 * GET abbia risposto fisserebbe i campi sui colori predefiniti, che poi non si
 * aggiornerebbero piu' nemmeno all'arrivo di quelli veri. Il padre lo monta
 * quando i dati ci sono, e da quel momento il valore iniziale e' quello giusto.
 */
function CompanyPalette({ palette, isAdmin }: { palette: BrandPalette; isAdmin: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();

  // Colori in modifica. Restano stringhe libere mentre si digita
  // l'esadecimale, e il salvataggio si abilita solo quando sono utilizzabili.
  const [primary, setPrimary] = useState(palette.primary);
  const [secondary, setSecondary] = useState(secondaryFieldValue(palette));
  const [savingPalette, setSavingPalette] = useState(false);

  const validColors = HEX.test(primary) && (secondary === '' || HEX.test(secondary));
  const savedSecondary = secondaryFieldValue(palette);
  const paletteChanged = primary !== palette.primary || secondary !== savedSecondary;
  // Anteprima: gli stessi calcoli che finiranno nelle CSS var.
  const preview = validColors ? derivaPalette({ primary, secondary: secondary || undefined }) : palette;

  async function handleSavePalette() {
    if (!validColors) return;
    setSavingPalette(true);
    try {
      // Secondario vuoto inviato come stringa vuota, non omesso: e' cosi' che
      // si CANCELLA un secondario scelto in precedenza tornando alla derivazione.
      await dispatch(updateCompanyPalette({ primary, secondary })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSavingPalette(false);
    }
  }

  /**
   * "Ripristina colori predefiniti" CANCELLA la palette della company: il DELETE
   * la rimuove a database e la risposta riporta i colori con cui il prodotto si
   * presenta di suo. Non è quindi un semplice svuotamento dei campi — dopo di
   * questo non c'è più niente da salvare, e chiunque entri vede i predefiniti.
   */
  async function handleResetPalette() {
    setSavingPalette(true);
    try {
      const restored = await dispatch(resetCompanyPalette()).unwrap();
      setPrimary(restored.primary);
      setSecondary(secondaryFieldValue(restored));
      toast({ severity: 'success', message: t('settings.company.paletteResetDone') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSavingPalette(false);
    }
  }

  return (
    <>
      {/* Ogni campo porta accanto a se' la propria anteprima: il primario e' un
          riempimento pieno col testo scelto per contrasto, il secondario una
          pastiglia chiara con testo scuro. Stare di fianco all'input, e non in un
          blocco a parte piu' in basso, dice a colpo d'occhio quale colore produce
          quale effetto. */}
      <div className="palette-fields">
        <PaletteField
          label={t('settings.company.palettePrimary')}
          hint={t('settings.company.palettePrimaryHint')}
          value={primary}
          onChange={setPrimary}
          disabled={!isAdmin}
          preview={
            <span
              className="palette-field__preview"
              style={{ background: preview.primary, color: preview.colorPrimary }}
            >
              {t('settings.company.palettePreviewPrimary')}
            </span>
          }
        />
        <PaletteField
          label={t('settings.company.paletteSecondary')}
          hint={
            secondary === ''
              ? t('settings.company.paletteSecondaryDerived')
              : t('settings.company.paletteSecondaryHint')
          }
          value={secondary}
          // A campo vuoto la pastiglia mostra il colore derivato: si vede cosa
          // si otterrebbe, e cliccandola si passa a una scelta esplicita.
          fallback={derivedSecondary(HEX.test(primary) ? primary : palette.primary)}
          placeholder={t('settings.company.paletteAuto')}
          onChange={setSecondary}
          disabled={!isAdmin}
          preview={
            <span
              className="palette-field__preview"
              style={{
                background: mixSrgb(preview.secondary, '#ffffff', 0.2),
                color: mixSrgb(preview.secondary, '#000000', 0.62),
              }}
            >
              {t('settings.company.palettePreviewSecondary')}
            </span>
          }
        />
      </div>

      {isAdmin ? (
        <div className="flex gap-sm">
          <Button
            variant="contained"
            onClick={handleSavePalette}
            disabled={!validColors || !paletteChanged || savingPalette}
          >
            {t('common.save')}
          </Button>
          <Button variant="text" onClick={handleResetPalette} disabled={savingPalette}>
            {t('settings.company.paletteReset')}
          </Button>
        </div>
      ) : (
        <p className="text-muted text-sm">{t('settings.company.readonlyPalette')}</p>
      )}
    </>
  );
}

/**
 * Un colore della paletta: pastiglia cliccabile (input nativo, quindi col
 * selettore del sistema) piu' l'esadecimale scrivibile a mano, per chi il
 * colore del brand ce l'ha gia' scritto da qualche parte.
 */
function PaletteField({
  label,
  hint,
  value,
  onChange,
  disabled,
  fallback,
  placeholder,
  preview,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  /** Colore mostrato quando il campo e' vuoto (secondario derivato). */
  fallback?: string;
  placeholder?: string;
  /** Anteprima dell'effetto del colore, mostrata in riga accanto al campo. */
  preview?: ReactNode;
}) {
  // Vuoto e' valido solo dove esiste un fallback: e' il caso "derivalo tu".
  const empty = value === '';
  const valid = HEX.test(value) || (empty && fallback !== undefined);
  return (
    <label className="palette-field">
      <span className="palette-field__label">{label}</span>
      <span className="palette-field__control">
        {/* A campo vuoto la pastiglia mostra il colore derivato, e mentre l'esadecimale scritto
            a mano e' incompleto tiene l'ultimo valido, cosi' non salta al nero. */}
        <ColorPicker
          label={label}
          value={HEX.test(value) ? value : fallback ?? '#000000'}
          onChange={onChange}
          disabled={disabled}
        />
        <input
          type="text"
          className={`palette-field__hex${valid ? '' : ' is-error'}`}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.trim())}
          disabled={disabled}
          spellCheck={false}
          aria-label={label}
        />
        {preview}
      </span>
      <span className="palette-field__hint">{hint}</span>
    </label>
  );
}
